import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Info, Shield, Layers, Ruler, Gem, Sparkles, MapPin, ArrowRight } from "lucide-react";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import usePageSEO, { makeServiceSchema } from "@/hooks/usePageSEO";
import PageFAQ from "@/components/PageFAQ";
import RelatedServices from "@/components/RelatedServices";
import { lvpHubFAQs } from "@/data/faqData";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import heroImg from "@/assets/material-lvp.webp";

const fade = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const LuxuryVinylPlank = () => {
  usePageSEO({
    title: "Luxury Vinyl Plank Flooring | CALI & Premium LVP Dealer",
    description: "Authorized luxury vinyl plank flooring dealer for CALI and premium waterproof LVP. 50 year warranties and FloorScore certified products.",
    path: "/luxury-vinyl-plank-flooring",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        makeServiceSchema(
          "Luxury Vinyl Plank Flooring Michigan",
          "SPC and WPC core specialists with 20-mil wear layers, realistic grain textures and architectural finish details including Ali Trims and TOP Step flush-mount vents."
        ),
        {
          "@type": "ItemList",
          "name": "LVP Brands & Services",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "CALI Vinyl Collections", "url": "https://1woodfloors.com/brands/cali" },
            { "@type": "ListItem", "position": 2, "name": "Armstrong Alterna", "url": "https://1woodfloors.com/brands/alterna" },
            { "@type": "ListItem", "position": 3, "name": "LVP Installation", "url": "https://1woodfloors.com/lvp-installation" },
            { "@type": "ListItem", "position": 4, "name": "Subfloor Leveling", "url": "https://1woodfloors.com/subfloor-leveling" },
          ],
        },
      ],
    },
  });

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>
        {/* ══ HERO ══ */}
        <section className="relative min-h-[560px] max-h-[800px] h-[75svh] flex items-end overflow-hidden bg-foreground">
          <PageBreadcrumb variant="overlay" items={[
            { label: "Home", to: "/" },
            { label: "Luxury Vinyl Plank" },
          ]} />
          <div className="absolute inset-0">
            <img width={1920} height={1080} loading="eager" fetchPriority="high" decoding="async"
              src={heroImg}
              alt="Luxury vinyl plank floor"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/95 via-foreground/50 to-foreground/20" />

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 pb-10 md:pb-16 px-5 md:px-10 lg:px-16 max-w-4xl"
          >
            <p className="text-[10px] md:text-[11px] font-semibold tracking-[.14em] uppercase text-primary mb-3 md:mb-4 flex items-center gap-2">
              <span className="w-7 h-px bg-primary inline-block" />
              Luxury Vinyl Plank Flooring
            </p>
            <h1 className="font-display text-[clamp(28px,5.5vw,58px)] font-semibold leading-[1.08] tracking-tight text-primary-foreground">
              Luxury Vinyl Plank Flooring Michigan
            </h1>
            <p className="font-display text-[clamp(18px,3vw,30px)] font-light text-primary-foreground/70 mt-2 leading-[1.2]">
              Waterproof, Pet-Friendly & Engineered to Last
            </p>
            <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/60 max-w-lg leading-relaxed">
              SPC and WPC core specialists. 20-mil wear layers. Realistic grain textures and architectural finish details that make every floor look like it cost twice as much as it did.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
              <QuoteModal
                trigger={
                  <button className="bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors text-center">
                    Book a $50 Estimate Visit
                  </button>
                }
              />
              <a href="tel:2483258853" className="border border-primary-foreground/25 text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:border-primary-foreground/60 transition-colors text-center">
                Call (248) 325-8853
              </a>
            </div>
          </motion.div>
        </section>

        {/* ══ SECTION 1 — FORENSIC FAST-FACT BOX ══ */}
        <section className="bg-background py-14 md:py-20">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}
              className="border border-primary/30 bg-primary/[.04] rounded-2xl p-6 md:p-10"
            >
              <div className="flex items-center gap-3 mb-6">
                <Info className="w-6 h-6 text-primary flex-shrink-0" />
                <h2 className="font-display text-lg md:text-xl font-semibold text-foreground">LVP Forensic Fast-Facts</h2>
              </div>
              <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
                {[
                  ["LVP (Luxury Vinyl Plank)", "A multi-layer synthetic flooring with a photographic wood image layer protected by a wear layer, installed as a floating floor"],
                  ["SPC Core (Stone Plastic Composite)", "Rigid, extremely durable, less forgiving to subfloor imperfections — tongue-and-groove failure risk without proper prep"],
                  ["WPC Core (Wood Plastic Composite)", "Slightly more flexible, marginally more forgiving — but same flatness standards apply"],
                  ["20-Mil Wear Layer", "Industry standard protection thickness for the image layer — determines how realistic the floor looks and how long it lasts"],
                ].map(([term, def]) => (
                  <div key={term}>
                    <dt className="text-sm font-semibold text-foreground mb-1">{term}</dt>
                    <dd className="text-sm text-muted-foreground leading-relaxed">{def}</dd>
                  </div>
                ))}
              </dl>
            </motion.div>
          </div>
        </section>

        {/* ══ SECTION 2 — THE CORE DECISION: SPC VS WPC ══ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Core Technology</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-primary-foreground mb-6">
                The Core Decision: SPC vs WPC
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-8 md:gap-12">
              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.1 }}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-primary-foreground mb-2">SPC — Stone Plastic Composite</h3>
                    <p className="text-sm text-primary-foreground/70 leading-relaxed">
                      SPC is harder and more rigid — it is the more durable choice, but because of that rigidity it is completely unforgiving to subfloor imperfections. If your subfloor has dips or crowns outside NWFA flatness tolerances, the tongue-and-groove locking system on an SPC floor will fail over time.
                    </p>
                  </div>
                </div>
              </motion.div>
              <motion.div {...fade} transition={{ duration: 0.6, delay: 0.2 }}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Layers className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-primary-foreground mb-2">WPC — Wood Plastic Composite</h3>
                    <p className="text-sm text-primary-foreground/70 leading-relaxed">
                      WPC is slightly more flexible and marginally more forgiving, but that does not mean subfloor preparation should be any less meticulous — industry flatness standards apply to both. This is why choosing the right installation contractor matters as much as choosing the right floor.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
            <motion.p {...fade} transition={{ duration: 0.6, delay: 0.3 }} className="mt-8 text-sm text-primary-foreground/70 leading-relaxed max-w-3xl">
              Both cores require meticulous{" "}
              <Link to="/subfloor-leveling" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                forensic subfloor preparation
              </Link>
              . The core choice changes the risk level, not the preparation standard.
            </motion.p>
          </div>
        </section>

        {/* ══ SECTION 3 — THE 20-MIL STANDARD ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Wear Layer Science</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-6">
                The 20-Mil Standard: What We Look For
              </h2>
              <div className="max-w-3xl space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  The 20-mil wear layer is the industry standard that protects the wood image printed into the product. But wear layer thickness alone is not the only measure — what we look for is a floor where the grain also stands out in the physical texture of the surface, not just in the printed image. High-definition embossing means the texture of the plank matches the grain of the image, making the floor feel and look like real wood.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  We spec LVP that delivers a genuine high-end wood aesthetic at a fraction of hardwood cost — that is the goal for every LVP project we install. Browse our{" "}
                  <Link to="/brands/cali" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                    CALI Vinyl collections
                  </Link>
                  {" "}to see the standard we hold.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══ SECTION 4 — RUN LENGTH AND TRANSITIONS ══ */}
        <section className="bg-foreground py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Installation Planning</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-primary-foreground mb-6">
                Run Length and Transitions
              </h2>
              <div className="max-w-3xl space-y-4">
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  Certain LVP products have manufacturer-specified maximum run lengths before a transition is required — typically in doorways or between rooms. We pay careful attention to these specifications during installation planning. Most installers use cheap plastic T-moldings that immediately undercut the look of the floor.
                </p>
                <p className="text-sm text-primary-foreground/70 leading-relaxed">
                  We source our transition profiles from{" "}
                  <a href="https://www.stairrods.us/ali-trims" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-[hsl(var(--gold-300))] underline underline-offset-2 transition-colors">
                    Ali Trims transition profiles
                  </a>
                  {" "}— a premium European manufacturer — giving every transition the same architectural quality as the floor itself.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ══ SECTION 5 — THE FINISHING DETAILS: MID-RANGE TO MASTERPIECE ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">The 1 Wood Floors Difference</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-6">
                The Finishing Details: Mid-Range to Masterpiece
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl mb-8">
                A mid-range LVP floor can look like a high-end architectural installation when the details are done correctly. We combine three finishing elements that most installers never consider:
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: <Gem className="w-7 h-7 text-primary" />,
                  title: "Ali Trims",
                  desc: "European-sourced metal transition profiles that replace the standard plastic T-moldings. Used at all doorway transitions and run-length breaks.",
                },
                {
                  icon: <Layers className="w-7 h-7 text-primary" />,
                  title: "TOP Step Flush-Mount Vents",
                  desc: "Floor vents that match the exact grain and color of the LVP floor. Instead of a metal or plastic grille sitting on top of the floor, the vent sits flush and disappears into the surface. Available in color-matched coupling vents or offset flush-mounted iron vents for an architectural contrast look.",
                },
                {
                  icon: <Sparkles className="w-7 h-7 text-primary" />,
                  title: "Consistent Detail Standard",
                  desc: "Every floor we install — LVP, hardwood, or laminate — gets the same level of attention to these finishing details. Nothing is an afterthought.",
                },
              ].map((item, i) => (
                <motion.div key={item.title} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="border border-border rounded-2xl p-6 md:p-8"
                >
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="font-display text-base font-semibold text-foreground mb-3">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>

            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.4 }} className="mt-8 flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-primary flex-shrink-0" />
              <p className="text-sm font-semibold text-foreground italic">
                The result is a floor that looks like it cost twice as much as it did.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ══ SECTION 6 — INSTALLATION SERVICE CTA ══ */}
        <section className="bg-primary py-10 md:py-14">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-[clamp(24px,3vw,36px)] font-semibold text-primary-foreground leading-[1.1] mb-2">
                From subfloor engineering to architectural <em className="italic font-normal text-[hsl(var(--gold-300))]">finish details.</em>
              </h3>
              <p className="text-[13px] font-light text-primary-foreground/65 leading-relaxed">
                From subfloor engineering to architectural finish details — our LVP installation process is documented in full.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full md:w-auto">
              <Link
                to="/lvp-installation"
                className="bg-primary-foreground text-primary px-7 py-3.5 rounded-xl text-xs font-bold tracking-[.05em] uppercase hover:-translate-y-px transition-transform whitespace-nowrap text-center"
              >
                View Our LVP Installation Process →
              </Link>
              <a href="tel:2483258853" className="border border-primary-foreground/30 text-primary-foreground px-7 py-3.5 rounded-xl text-xs font-semibold tracking-[.05em] hover:border-primary-foreground hover:text-primary-foreground transition-colors whitespace-nowrap text-center">
                📞 (248) 325-8853
              </a>
            </div>
          </div>
        </section>

        {/* ══ SECTION 7 — BRANDS WE CARRY ══ */}
        <section className="bg-background py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary mb-2">Authorized Dealer</p>
              <h2 className="font-display text-[clamp(24px,4vw,44px)] font-semibold leading-[1.08] text-foreground mb-8">
                Brands We Carry
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                { name: "CALI Vinyl", to: "/brands/cali", desc: "11 collections including Longboards, Legends and Trestles. Authentic hardwood visuals in waterproof LVP." },
                { name: "Armstrong Alterna", to: "/brands/alterna", desc: "Engineered tile alternative. 100% waterproof, warmer than ceramic, grouted or non-grouted." },
              ].map((brand, i) => (
                <motion.div key={brand.name} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }}>
                  <Link to={brand.to} className="group block border border-border rounded-2xl p-6 md:p-8 hover:bg-muted/50 hover:border-border/80 transition-all h-full">
                    <h3 className="font-display text-lg font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">{brand.name}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">{brand.desc}</p>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary group-hover:text-[hsl(var(--gold-300))] transition-colors">
                      Explore Collection <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ SECTION 8 — RELATED SERVICES ══ */}
        <RelatedServices
          heading="Complete your LVP project."
          subheading="Related Services"
          links={[
            { title: "Subfloor Leveling & Repair", desc: "The foundation every LVP floor requires", to: "/subfloor-leveling", tag: "Foundation" },
            { title: "Alterna Tile Installation", desc: "The tile alternative that flows seamlessly from your LVP", to: "/alterna-tile-installation", tag: "Tile" },
            { title: "NWFA Certified Inspections", desc: "Independent forensic analysis for LVP failures", to: "/inspections", tag: "Inspections" },
          ]}
        />

        {/* ══ SECTION 9 — SERVICE AREAS ══ */}
        <section className="bg-background py-14 md:py-20">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12">
            <motion.div {...fade} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-5 h-5 text-primary flex-shrink-0" />
                <p className="text-[10px] font-semibold tracking-[.18em] uppercase text-primary">Service Areas</p>
              </div>
              <h2 className="font-display text-[clamp(22px,3.5vw,34px)] font-semibold leading-[1.12] text-foreground mb-6">
                LVP Installation Across Michigan
              </h2>
            </motion.div>

            <div className="flex flex-wrap gap-3">
              {[
                { name: "Livonia", to: "/" },
                { name: "Northville", to: "/hardwood-flooring/northville-mi" },
                { name: "Plymouth", to: "/hardwood-flooring/plymouth-mi" },
                { name: "Ann Arbor", to: "/hardwood-flooring/ann-arbor-mi" },
                { name: "Birmingham", to: "/hardwood-flooring/birmingham-mi" },
              ].map((city) => (
                <Link key={city.name} to={city.to} className="px-5 py-2.5 rounded-full border border-border text-sm font-medium text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                  {city.name}
                </Link>
              ))}
            </div>
            <Link to="/hardwood-flooring" className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-primary hover:text-[hsl(var(--gold-300))] transition-colors">
              View all 29 Michigan service areas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* ══ SECTION 10 — REVIEWS ══ */}
        <GoogleReviewsCarousel contextTags={["installation"]} count={4} />

        {/* ══ SECTION 11 — FAQ ══ */}
        <PageFAQ suppressJsonLd
          faqs={lvpHubFAQs}
          heading={<>LVP flooring questions <em className="italic font-normal text-primary">answered.</em></>}
          subheading="Luxury Vinyl Plank FAQs"
        />
      </main>
      <SiteFooter />
    </div>
  );
};

export default LuxuryVinylPlank;
